import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'

// Buttons are the main app's, class for class: the filled rose primary
// ("New Collection"), the outlined "+ New", and the nav's quiet idle style.
// rounded-xl is 16px (the app's radius scale), so at these heights they read
// as soft pills. Interface text stays Manrope (Niki, 9/30).
export const btn = {
  primary:
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-accent px-4 py-1.5 text-[14px] font-medium text-accent-foreground shadow-sm shadow-accent/10 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
  outline:
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-border/40 px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-all hover:border-accent/40 hover:bg-muted/20 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
  ghost:
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
  /** The app's page-level call to action ("New Collection"): the primary, larger. */
  primaryLg:
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-accent px-5 py-2.5 text-[14px] font-medium text-accent-foreground shadow-sm shadow-accent/10 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
  /** Its quieter partner ("Cancel" in the app's dialogs). */
  outlineLg:
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-border/40 px-5 py-2.5 text-[14px] font-medium text-muted-foreground transition-all hover:border-accent/40 hover:bg-muted/20 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
}

/** The app's filter chip (Library): rose when chosen, a faint outline when not. */
export function chip(active: boolean): string {
  return `inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl border px-3 py-1.5 text-[13px] font-medium transition-all ${
    active ? 'border-accent/30 bg-accent/15 text-accent' : 'border-border/40 bg-card/60 text-muted-foreground hover:border-border/60 hover:text-foreground'
  }`
}

export const inputCls =
  'w-full rounded-xl border border-border/40 bg-card/60 px-3 py-2.5 text-[13px] font-normal text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-accent/40 focus:bg-card'

export const labelCls = 'text-[13px] font-semibold tracking-[0.02em] text-muted-foreground'

/**
 * A person's photo, or the disc Burrow shows when there isn't one (the app's
 * ProfileAvatar): rose, with their initial in Fraunces italic.
 */
export function Avatar({
  src,
  name,
  size = 22,
  className = '',
}: {
  src?: string | null
  name?: string | null
  size?: number
  className?: string
}) {
  const style = { width: size, height: size }
  if (src) return <img src={src} alt="" style={style} className={`shrink-0 rounded-full object-cover ${className}`} />
  const initial = (name ?? '').trim().charAt(0).toUpperCase() || '?'
  return (
    <span
      aria-hidden
      style={{ ...style, fontSize: Math.max(9, Math.round(size * 0.46)) }}
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-accent font-display font-medium italic leading-none text-accent-foreground ${className}`}
    >
      {initial}
    </span>
  )
}

export function Switch({
  checked,
  onChange,
  label,
  className = '',
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-2 text-inherit ${className}`}
    >
      <span>{label}</span>
      <SwitchTrack on={checked} />
    </button>
  )
}

/** The app's switch (ui/switch.tsx): plum when on, the warm grey track when off. */
export function SwitchTrack({ on }: { on: boolean }) {
  return (
    <span aria-hidden className={`relative inline-block h-[18px] w-8 shrink-0 rounded-full transition-colors ${on ? 'bg-primary' : 'bg-switch-background'}`}>
      <span
        className={`absolute top-px size-4 rounded-full transition-[left] ${
          on ? 'left-[15px] bg-card dark:bg-primary-foreground' : 'left-px bg-card dark:bg-card-foreground'
        }`}
      />
    </span>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" className="flex min-h-[50vh] items-center justify-center font-display text-[15px] italic text-muted-foreground">
      {label}
    </div>
  )
}

export function Notice({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-[520px] flex-col items-center gap-3 px-4 py-24 text-center">
      <h1 className="m-0 font-display text-[30px]/[1.15] font-normal text-primary">{title}</h1>
      {body && <p className="m-0 text-[14px]/[1.55] text-muted-foreground">{body}</p>}
      {action}
    </div>
  )
}

export function MobileSheet({
  label,
  onClose,
  sidebar = false,
  children,
}: {
  label: string
  onClose: () => void
  /** The side panels' sheet (Thoughts, Taken): the app's sidebar tone, as the desktop rail. */
  sidebar?: boolean
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label={label}>
      <button type="button" aria-label="Close" className="absolute inset-0 bg-foreground/30" onClick={onClose} />
      <div className={`absolute inset-x-0 bottom-0 flex h-[82dvh] flex-col overflow-hidden rounded-t-2xl ${sidebar ? 'bg-sidebar' : 'bg-background'} pb-[env(safe-area-inset-bottom)] shadow-[var(--shadow-sheet)]`}>
        <div aria-hidden className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-foreground/15" />
        {children}
      </div>
    </div>
  )
}

/**
 * Props for a centred sheet's scrolling wrapper, so a click on the dim space
 * around the sheet closes it. The wrapper covers the backdrop, so the click
 * has to be caught there: only a press that both starts and ends on the
 * wrapper itself counts (a drag out of a text field doesn't close the sheet).
 */
export function useBackdropClose(onClose: () => void) {
  const downOnBackdrop = useRef(false)
  return {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      downOnBackdrop.current = e.target === e.currentTarget
    },
    onClick: (e: ReactMouseEvent<HTMLElement>) => {
      const close = downOnBackdrop.current && e.target === e.currentTarget
      downOnBackdrop.current = false
      if (close) onClose()
    },
  }
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong.'
}
